import { Stack } from '@chronosphereio/chrono-ui';
import type { ReactNode } from 'react';

export interface QueryEditorToolbarProps {
  children?: ReactNode;
  /** Accessible name for the toolbar region (e.g. "Query editor toolbar"). */
  'aria-label'?: string;
}

/**
 * Top toolbar row inside `QueryEditor.Container`. Full width, `justifyContent="space-between"` so a **single** leading
 * cluster (e.g. mode toggle) stays left and `QueryEditor.ToolbarActions` stays right — matches metrics explorer.
 *
 * If you have several leading controls, wrap them in one horizontal `Stack` so this row still has **two** flex
 * children (leading stack + `ToolbarActions`). Otherwise `space-between` distributes three+ siblings across the bar.
 *
 * `flexWrap="wrap"` lets the actions cluster move to the next line on narrow widths while `ToolbarActions` keeps
 * `marginLeft: auto` so it stays right-aligned on its row.
 */
export function QueryEditorToolbar({
  children,
  'aria-label': ariaLabel = 'Query editor toolbar',
}: QueryEditorToolbarProps) {
  return (
    <Stack
      role="toolbar"
      aria-label={ariaLabel}
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      width="100%"
      p={1}
      bgcolor="background.paper"
    >
      {children}
    </Stack>
  );
}
